// Per-ingestor config and secret schema definitions for the web admin
// form. The api validates against the plugin's zod schema; this is the
// mirror for the UI. If a type has no entry here, the form falls back
// to a generic "key/value pairs" JSON editor.

export interface ConfigField {
  name: string;
  label: string;
  type: 'text' | 'url' | 'select' | 'boolean' | 'number';
  required?: boolean;
  placeholder?: string;
  help?: string;
  options?: Array<{ value: string; label: string }>;
  defaultValue?: string | number | boolean;
}

export interface IngestorUiSchema {
  config: ConfigField[];
  secrets: ConfigField[];
}

export const INGESTOR_UI_SCHEMAS: Record<string, IngestorUiSchema> = {
  github: {
    config: [
      { name: 'repo', label: 'Repository (owner/name)', type: 'text', required: true, placeholder: 'forbury/platform', help: 'Default repository for events without a repo in the payload.' },
      { name: 'autoCreateWebhook', label: 'Auto-register webhook on first event', type: 'boolean', defaultValue: false, help: 'Reserved for v1.1 — server-side webhook provisioning via the GitHub API. Currently the webhook must be set up in the GitHub repo settings.' },
    ],
    secrets: [
      { name: 'webhookSecret', label: 'Webhook secret', type: 'text', required: true, help: 'Used to verify X-Hub-Signature-256. Set the same value on the GitHub side. Must be ≥16 chars, include 3+ character classes, and not be a common-weak secret. Generate with: openssl rand -base64 32' },
      { name: 'personalAccessToken', label: 'Personal access token (PAT)', type: 'text', required: false, help: 'Optional. Needed if the API will register the webhook itself (v1.1).' },
    ],
  },
  jira: {
    config: [
      { name: 'baseUrl', label: 'Jira base URL', type: 'url', required: true, placeholder: 'https://yourorg.atlassian.net', help: 'Used for the test-connection ping and to build the JQL.' },
      // M5.7 follow-up: the UI now exposes auth mode + email, which
      // are required for Jira Cloud. Previously the schema was
      // missing these fields, so admin had to use the raw-JSON
      // editor to set them, and a fresh UI-created ingestor would
      // silently fail to authenticate. With the schema in place,
      // the api's zod refine() can also reject incomplete configs
      // at the PATCH endpoint.
      { name: 'authMode', label: 'Auth mode', type: 'select', defaultValue: 'basic', options: [
        { value: 'basic', label: 'Basic (email + API token) — Jira Cloud' },
        { value: 'bearer', label: 'Bearer (PAT) — Jira Server / Data Center' },
      ], help: 'Jira Cloud (atlassian.net) requires Basic auth with an Atlassian account email and an API token. Jira Server / Data Center with PATs uses Bearer.' },
      { name: 'email', label: 'Atlassian account email', type: 'text', required: true, placeholder: 'you@yourorg.com', help: 'Required for Basic auth. The Atlassian account that owns the API token (not necessarily the Jira instance admin).' },
      { name: 'projectKey', label: 'Default project key', type: 'text', required: false, placeholder: 'FORB', help: 'Default project to scope events to. Events with a project in the payload override this.' },
      // M5.7 follow-up: poll interval is now exposed in the
      // admin UI. Was previously hard-coded to 2 min for the
      // Forbury Jira instance, which turned out to be aggressive
      // — the JQL bug had us re-fetching 50+ issues per cycle
      // and the dedup masked the wasted work. Default 10 min is
      // a good baseline for light-to-medium Jira activity; lower
      // it for hot projects (incident tracking) or raise it for
      // cold ones. Min 1 (per the zod schema).
      { name: 'pollIntervalMinutes', label: 'Poll interval (minutes)', type: 'number', defaultValue: 10, help: 'How often the poller queries Jira for updates. Default 10 min. Lower = more responsive, more Jira API calls. Raise for low-activity projects; lower for hot ones (e.g. incident tracking).' },
      // Story points ingestor config. Jira's "story point" /
      // estimate custom field varies per project (the common
      // default is customfield_10016 but operators commonly
      // rename it or use a different number). The default here
      // matches the typical Jira Cloud "Software" project
      // setup; operators can override per ingestor via this field.
      // The ingested value lands in the event payload as
      // `points: number | null` and is visible in the audit log +
      // on the scoring engine's payload.* condition DSL.
      { name: 'storyPointsField', label: 'Story points custom field ID', type: 'text', defaultValue: 'customfield_10024', required: false, placeholder: 'customfield_10024', help: 'Custom field ID Jira uses for story points. Must look like customfield_<numeric id>. Default customfield_10024 (the operator\'s actual Jira project; corrected in v0.7.26 from the Atlassian default 10016). Operators using T-shirt sizing (S/M/L) should leave this empty or use a number-typed field; non-numeric values become null on the event payload, never a poll failure.' },
      // Phase 1.5 (ADR-0024): the "Import from Jira" admin
      // surface reads this field to derive the team's sprints +
      // their issue membership. If unset, the import endpoint
      // returns 503 'sprint_field_not_configured' and the
      // operator authors adventures manually.
      { name: 'sprintField', label: 'Sprint custom field ID', type: 'text', defaultValue: 'customfield_10020', required: false, placeholder: 'customfield_10020', help: 'Custom field ID Jira uses for the Sprint picker. Default customfield_10020 (Atlassian Scrum default). Phase 1.5 import-from-Jira surface uses this field. Leave empty to disable the import feature.' },
    ],
    secrets: [
      { name: 'webhookSecret', label: 'Webhook secret', type: 'text', required: true, help: 'Used to verify X-Hub-Signature (HMAC-SHA1 or SHA256).' },
      { name: 'apiToken', label: 'API token', type: 'text', required: false, help: 'Atlassian API token (for Basic auth) or PAT (for Bearer). Needed for the test-connection ping and for fetching user info.' },
    ],
  },
  mock: {
    config: [
      { name: 'displayLabel', label: 'Display label', type: 'text', required: false, placeholder: 'Local dev mock', help: 'Free-form label shown on the instance detail page.' },
    ],
    secrets: [],
  },
  manual: {
    config: [
      { name: 'displayLabel', label: 'Display label', type: 'text', required: false, placeholder: 'Surveys, kudos, …', help: 'Free-form label to distinguish this instance from other manual ingestors (e.g. "Surveys", "Kudos", "Promo adjustments").' },
    ],
    secrets: [],
  },
};

export function getIngestorSchema(type: string): IngestorUiSchema | undefined {
  return INGESTOR_UI_SCHEMAS[type];
}
